/**
 * Finishing a piece of work: propose it, then clear up after it merged.
 *
 * The bookend to the work picker in the bar. Starting work raised a GitLab
 * issue and cut a branch; every save since has been a commit on that branch.
 * This screen turns the branch into a merge request that CLOSES the issue,
 * and - once somebody has merged it - deletes the branch's working database,
 * which was only ever a cache of a branch that no longer exists.
 *
 * BEST CAN NOW MERGE, and the sentence that used to be here said it could not:
 * "GitLab's protected-branch rules decide who may press the button, so there is
 * nothing here for BEST to gate". The premise was right and the conclusion did
 * not follow. GitLab enforces those same rules on its merge endpoint, and BEST
 * calls it with the signed-in person's own token - so the button forwards to
 * exactly the gate that would refuse them in GitLab's own UI. There is still
 * nothing here for BEST to gate, which is why there is no permission check on
 * it beyond the one every write already carries. Issue #16.
 *
 * What the screen DOES do is show what GitLab reports and say so when the
 * button cannot work (issue #67). The open merge request carries GitLab's own
 * `merge_status` (what is in the way) and `can_merge` (whether THIS person may
 * merge it); the card words the first, and the Merge button explains itself
 * from both. That is reading GitLab's answer, not holding a rule: BEST lists no
 * roles or conditions, and a status or permission it does not recognize or was
 * not given leaves the button enabled, because GitLab decides when it is
 * pressed. A refusal then arrives as a 403 ("you may not merge here") or a 409
 * ("not yet") and stays on the card.
 *
 * The same card lists who it is assigned to and who was asked to review it,
 * with a "can merge" mark on the reviewers GitLab says can. When the Merge
 * button is off because the caller cannot merge, its reason adds who can (or
 * that nobody asked can). Under Reviewers, a "Code owners:" line lists whoever
 * GitLab names as owning the files the change touches (BEST asks GitLab, it does
 * not read CODEOWNERS), with the section each owns and a quiet "asked" mark, and
 * a writer's "Ask them" adds the ones not yet asked. A new merge request is
 * opened with the owners already asked; this line is how they are seen, and how
 * the rest are asked after the fact. Those are sentences and marks, not controls: nothing
 * here is required of anyone. The sentence is drawn on the card as well as in
 * the tooltip, and the Reviewers line has a "Change" button for writers that
 * reopens the propose dialog as a reviewers editor.
 *
 * The screen also shows WHAT THE BRANCH CHANGES - issue #30 - which is a
 * different question from the commit history beneath it. History says what
 * landed on the branch and links out to GitLab, because GitLab is the record of
 * what happened to the files. The change list says which model elements moved
 * and which requirements now need re-reviewing because they link to one that
 * did. Two questions; they are drawn as two panels and must not be merged.
 *
 * Nothing polls. GitLab is asked when this screen renders and when the person
 * asks again, which is why "Check again" is a control rather than a spinner.
 */

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import CircularProgress from '@mui/material/CircularProgress'
import DifferenceIcon from '@mui/icons-material/Difference'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import HistoryIcon from '@mui/icons-material/History'
import LaunchIcon from '@mui/icons-material/Launch'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import Paper from '@mui/material/Paper'
import RefreshIcon from '@mui/icons-material/Refresh'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ApprovalRow from '../components/common/ApprovalRow'
import BaselinesSection from '../components/common/BaselinesSection'
import ChangeList from '../components/common/ChangeList'
import ChangesExportMenu from '../components/common/ChangesExportMenu'
import CommitHistory from '../components/common/CommitHistory'
import ConfirmDialog from '../components/common/ConfirmDialog'
import DiscussionPanel from '../components/common/DiscussionPanel'
import EditElementDialog from '../components/common/EditElementDialog'
import { editTargetForThread } from '../components/common/elementThreads'
import type { EditTarget } from '../components/common/elementThreads'
import EmptyState from '../components/common/EmptyState'
import { CanMergeMark } from '../components/common/PeoplePicker'
import ProposeMergeDialog from '../components/common/ProposeMergeDialog'
import { ApiError, api, signInUrl } from '../api'
import type {
  CodeOwner,
  CommitHistoryPayload,
  FinishResult,
  MergeRequest,
  MergeState,
  ModelChanges,
  OtherProposal,
  PersonMergeHint,
} from '../schema'
import { useCanWrite, usePrincipal, useSchema } from '../state/AppData'
import { viewedBaseline } from '../state/baselineRef'
import { useProject } from '../state/Project'
import { useToasts } from '../state/Toasts'
import { MONOSPACE } from '../theme'
import { mergeBlocker } from './merge-blocker'
import type { MergeBlocker } from './merge-blocker'

/**
 * GitLab's answer to pressing Merge when it said no, kept on the merge request's
 * card until the person asks again.
 *
 * Not a toast and not the screen-wide `Outcome`: it is about THIS merge request
 * and has to be there when they look back at it. `info` is a 409, "not yet",
 * which is ordinary; `warning` is a 403, "not you", which waiting does not fix.
 */
interface Refusal {
  severity: 'info' | 'warning'
  title: string
  detail: string
}

/**
 * The answer to the last thing the person did.
 *
 * `info` carries the 409s, which are ordinary states rather than faults: "the
 * branch is at the same commit as main", "it is still open for review". They
 * are things to say calmly, and a red banner would say the tool went wrong.
 */
interface Outcome {
  severity: 'info' | 'success'
  title: string
  detail: string
}

/** The state chip beside the heading, so the situation reads at a glance. */
function situationChip(open: MergeRequest | null, merged: MergeRequest | null) {
  if (open) return <Chip label="Under review" color="primary" size="small" />
  if (merged) return <Chip label="Merged" color="success" size="small" />
  return <Chip label="Not proposed" size="small" variant="outlined" />
}

export default function VersionControl() {
  const { current, branch, issue, singleDatabase, reload } = useProject()
  const principal = usePrincipal()
  const { allowed, reason } = useCanWrite()
  const toasts = useToasts()
  const schema = useSchema()

  const slug = current?.slug ?? ''
  const target = current?.default_branch || 'main'
  const onDefaultBranch = Boolean(current) && (branch || target) === target
  // A baseline (issue #97) is a fixed copy of a tag, not a branch: it has no merge
  // request, history or change list to read, and nothing here may act on it.
  const baseline = viewedBaseline(principal.baseline, branch)

  const [state, setState] = useState<MergeState | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [proposing, setProposing] = useState(false)
  // The same dialog in its other mode: reviewers of the merge request that is
  // already open.
  const [changingReviewers, setChangingReviewers] = useState(false)
  // Which mode the dialog is in is kept apart from whether it is open, so
  // closing it does not swap its contents while it fades out.
  const [dialogMode, setDialogMode] = useState<'propose' | 'reviewers'>('propose')
  const [askingOwners, setAskingOwners] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [finished, setFinished] = useState<FinishResult | null>(null)
  const [history, setHistory] = useState<CommitHistoryPayload | null>(null)
  // The default branch's history is a window onto a long list: this many of the
  // most recent, and which page of them. A work branch has neither.
  const [historySize, setHistorySize] = useState(50)
  // The page remembers the branch it was chosen on, so moving to another branch
  // starts again at the newest without a second effect to reset it - that one
  // would fetch the old page first and race the right one.
  const [pageAt, setPageAt] = useState({ branch: '', page: 1 })
  const historyPage = pageAt.branch === branch ? pageAt.page : 1
  const [historyError, setHistoryError] = useState<ApiError | null>(null)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [changes, setChanges] = useState<ModelChanges | null>(null)
  const [changesError, setChangesError] = useState<ApiError | null>(null)
  const [changesLoading, setChangesLoading] = useState(true)
  // How many links out the review walk goes. One by default: the direct
  // dependants of what changed are what anybody wants first, and the list gets
  // long fast - past three hops nearly every requirement in a program is
  // reachable from nearly every other, which the server clamps.
  const [depth, setDepth] = useState(1)
  const [merging, setMerging] = useState(false)
  // Issue #110: the element being edited in a dialog, and a counter that makes
  // the discussion panel re-read after a save.
  const [editing, setEditing] = useState<EditTarget | null>(null)
  const [discussionRefresh, setDiscussionRefresh] = useState(0)

  // Reading the repository needs a GitLab token, which an anonymous caller
  // does not have. Asking anyway would answer 401, and the client turns every
  // 401 into "your session has expired" - which is the wrong thing to tell
  // somebody who never had one.
  const readable = Boolean(slug) && !principal.anonymous && !baseline

  const load = useCallback(() => {
    if (!readable) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    // A refusal describes the merge request as it was when the button was
    // pressed. Asking again is how the person finds out whether that is still
    // true, so the old answer must not sit there contradicting the new one.
    setRefusal(null)
    api
      .mergeState(slug)
      .then((payload) => {
        setState(payload)
        // Kept rather than cleared when the answer carries none: only
        // proposing computes the closing warnings, so a re-read would
        // otherwise erase something that is still true.
        setWarnings((previous) =>
          payload.open?.warnings.length ? payload.open.warnings : previous,
        )
      })
      .catch((failure: ApiError) => setError(failure))
      .finally(() => setLoading(false))
  }, [readable, slug])

  /**
   * The branch's own commits.
   *
   * A second request rather than a field on the merge state, because the two
   * answer different questions and fail separately: GitLab can list a branch's
   * commits while having nothing to say about a merge request, and a history
   * that failed to load must not take the propose button down with it.
   *
   * The empty ref is deliberate - the API resolves it to the branch BEST
   * already remembers for this person, the same convention mergeState uses, so
   * the two cannot end up describing different branches.
   *
   * The size and page are sent only on the default branch (issue #101): there
   * they choose a window onto a long history, and on a work branch the server
   * ignores them, because the branch's own commits are short and a page
   * boundary in them would hide work.
   */
  const loadHistory = useCallback(() => {
    if (!readable) {
      setHistoryLoading(false)
      return
    }
    setHistoryLoading(true)
    setHistoryError(null)
    api
      .commits(
        slug,
        onDefaultBranch ? { limit: historySize, page: historyPage } : {},
      )
      .then((payload) => setHistory(payload))
      .catch((failure: ApiError) => setHistoryError(failure))
      .finally(() => setHistoryLoading(false))
  }, [readable, slug, onDefaultBranch, historySize, historyPage])

  /**
   * What the branch changes against the branch it would merge into.
   *
   * A third request, and separate from both the others for the same reason
   * they are separate from each other: it fails on its own terms. A program
   * whose target branch has never been imported answers 409 here and answers
   * perfectly well everywhere else, and a comparison that cannot be made must
   * not take the propose button - or the history - down with it.
   */
  const loadChanges = useCallback(() => {
    if (!readable) {
      setChangesLoading(false)
      return
    }
    setChangesLoading(true)
    setChangesError(null)
    api
      .changes(slug, { depth })
      .then((payload) => setChanges(payload))
      .catch((failure: ApiError) => {
        setChanges(null)
        setChangesError(failure)
      })
      .finally(() => setChangesLoading(false))
  }, [readable, slug, depth])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    loadChanges()
  }, [loadChanges, branch])

  // Re-read when the person moves to another branch: the history is the one
  // thing on this screen that is about the branch's PAST rather than about
  // what is proposed for it, so a stale list here is a wrong answer rather
  // than an out-of-date one.
  useEffect(() => {
    loadHistory()
  }, [loadHistory, branch])

  // Everything above the history is about ONE branch, so changing branch
  // drops what was said about the last one. `finished` deliberately survives:
  // finishing is itself what moved the person back to the default branch, and
  // its summary is the answer to what just happened.
  useEffect(() => {
    setWarnings([])
    setOutcome(null)
    setRefusal(null)
  }, [branch])

  const proposed = (proposal: MergeRequest) => {
    setProposing(false)
    setWarnings(proposal.warnings ?? [])
    setFinished(null)
    setOutcome(
      proposal.created
        ? {
            severity: 'success',
            title: `Opened !${proposal.iid}`,
            detail: `${proposal.title} is waiting for review. Merge it when it is ready, then finish up.`,
          }
        : {
            // The second click, which people make. The useful answer is the
            // merge request rather than a complaint about it existing.
            severity: 'info',
            title: `Already proposed as !${proposal.iid}`,
            detail: `${branch} was already open for review, so nothing new was opened.`,
          },
    )
    toasts.notify(
      proposal.created ? `!${proposal.iid} opened` : `!${proposal.iid} was already open`,
      proposal.created ? 'success' : 'info',
    )
    load()
  }

  /**
   * Reviewers were changed on the open merge request. Not a proposal: no
   * "Opened" banner and no new warnings, just a toast and a re-read so the card
   * shows who GitLab now holds.
   */
  const reviewersSaved = (saved: MergeRequest) => {
    setChangingReviewers(false)
    toasts.succeeded(`Reviewers updated on !${saved.iid}`)
    load()
  }

  /**
   * Add the owners nobody has asked yet to the open merge request's reviewers.
   *
   * Sends ONLY `reviewer_ids`: the people already on it plus the missing owners
   * (the author is left out, see unaskedOwners). That is the existing propose
   * route's "set these reviewers" path - the same request the Change dialog
   * makes - so nobody already asked is dropped, and nothing else about the merge
   * request is touched. A deliberate click, never automatic: someone may have
   * removed an owner on purpose and BEST stores nothing to remember that.
   *
   * A refusal shows as a toast, like the other actions on this screen that have
   * no dialog open to hold the message.
   */
  const askOwners = async (proposal: MergeRequest) => {
    const missing = unaskedOwners(proposal)
    if (missing.length === 0) return
    setAskingOwners(true)
    try {
      await api.proposeMerge(slug, {
        reviewer_ids: [
          ...(proposal.reviewers ?? []).map((person) => person.id),
          ...missing.map((owner) => owner.id),
        ],
      })
      toasts.succeeded(`Asked ${missing.map((owner) => owner.name).join(', ')} to review !${proposal.iid}`)
      load()
    } catch (failure) {
      toasts.failed(
        `Could not ask the code owners: ${(failure as ApiError).message || 'GitLab refused'}`,
      )
    } finally {
      setAskingOwners(false)
    }
  }

  /**
   * Merge, from here rather than from GitLab.
   *
   * Two refusals are answers, not faults, and both stay on the card rather
   * than passing as a toast:
   *   409  GitLab saying 'not yet' - a conflict, an unmet approval, a pipeline
   *        still running. Information, the same reading `finish` takes of its
   *        own 409; it is the ordinary outcome of pressing the button early.
   *   403  GitLab saying this person may not merge into that branch. NOT an
   *        expired session (that is 401, handled in the API client) and NOT
   *        "GitLab is down": nothing here will fix it, somebody else has to
   *        merge.
   *
   * Merging does not clear up. `finish` is still what deletes the branch's
   * working database, because taking somebody's working copy away in the same
   * click they merged is not what the button says it does.
   */
  const merge = async () => {
    setBusy(true)
    setOutcome(null)
    setRefusal(null)
    try {
      const result = await api.acceptMerge(slug)
      setOutcome({
        severity: 'success',
        title: `!${result.iid} merged`,
        detail: `${result.branch} is in ${target}. Finish up when you are ready — that deletes the branch's working database and re-reads ${target}.`,
      })
      toasts.succeeded(`!${result.iid} merged`)
      load()
    } catch (failure) {
      const refused = failure as ApiError
      if (refused.status === 409) {
        setRefusal({ severity: 'info', title: 'Not merged', detail: refused.message })
      } else if (refused.status === 403) {
        setRefusal({ severity: 'warning', title: 'Not allowed to merge', detail: refused.message })
      } else {
        toasts.failed(refused.message)
      }
    } finally {
      setMerging(false)
      setBusy(false)
    }
  }

  const finish = async () => {
    setBusy(true)
    setOutcome(null)
    try {
      const result = await api.finishWork(slug)
      // The summary below says what happened, so nothing is said here as
      // well: two banners repeating one another read as two events.
      setFinished(result)
      setWarnings([])
      toasts.succeeded(`${result.branch} finished`)
      // The branch, the issue and the model all just changed under the bar.
      await reload()
      load()
    } catch (failure) {
      const refused = failure as ApiError
      if (refused.status === 409) {
        // Not a failure: the work has not landed yet, or it is still open for
        // review. Saying so plainly is the whole response - and the detail
        // names the merge request, which is what somebody needs next.
        setOutcome({ severity: 'info', title: 'Not merged yet', detail: refused.message })
      } else {
        toasts.failed(refused.message)
      }
    } finally {
      setConfirming(false)
      setBusy(false)
    }
  }

  if (singleDatabase || !current) {
    return (
      <EmptyState
        icon={<MergeTypeIcon />}
        title="No repository behind this model"
        description="BEST was started against a database directly, so there is no branch, no merge request and nothing to propose. Register a program with a GitLab repository to use this screen."
      />
    )
  }

  if (principal.anonymous) {
    return (
      <EmptyState
        icon={<MergeTypeIcon />}
        title="Sign in to see the review"
        description="Reading the repository uses your own GitLab access, so BEST cannot show merge requests until it knows who you are."
        actions={[{ label: 'Sign in with GitLab', href: signInUrl() }]}
      />
    )
  }

  const open = state?.open ?? null
  const merged = state?.merged ?? null

  // Both buttons are always drawn and at most one is live, the same rule the
  // forms follow: a control that disappears between states is indistinguishable
  // from a page that has broken.
  //
  // The default branch is NOT tested again here. useCanWrite already refuses
  // it, in the same words every other screen uses, and stating the rule in two
  // places is how the two come to disagree.
  const proposeReason = !allowed
    ? reason
    : open
      ? `Already proposed as !${open.iid}. Merge it, then finish up here.`
      : ''
  // Finishing stays live when nothing has merged, deliberately. BEST cannot
  // tell "still in progress" from "the branch was deleted and the work
  // abandoned" - the endpoint answers both with two nulls, and only finish
  // knows the difference. So the 409 says which, in better words than a guess
  // here would, and the abandoned case is not locked out to prevent it.
  const finishReason = !allowed
    ? reason
    : open
      ? `!${open.iid} is still open for review. Merge it first.`
      : ''
  // What is in the way of merging, from GitLab's own `merge_status`.
  //
  // `has_conflicts` is NOT what this is judged by: GitLab mirrors it from its
  // deprecated merge status, so it is true for ANY merge request that cannot
  // merge, an empty one included, and used to show "conflicts" where there were
  // none. It is read only as a fallback for a payload that has no
  // `merge_status` KEY at all (an older server), where it was the only signal
  // and the previous behavior is kept. A `merge_status` of null is the new
  // server saying GitLab did not report one, and that is "no blocker known",
  // not a cue to go back to the field that was wrong.
  const status = open
    ? open.merge_status === undefined
      ? open.has_conflicts === true
        ? 'conflict'
        : null
      : open.merge_status
    : null
  const blocker = open ? mergeBlocker(status, open.target) : null
  const cannotMerge = open?.can_merge === false
  const blockedFromMerging = Boolean(blocker?.blocking)
  // Merging is live only while something is open to merge AND GitLab has not
  // said it cannot happen. Both signals are GitLab's: `can_merge` is whether
  // THIS person may, and a blocker with `blocking` set is one pressing the
  // button cannot get past. BEST holds no list of who may merge or what must
  // be true first. `can_merge` null (GitLab did not say), a status still being
  // checked and a status BEST does not recognize all leave the button live,
  // because GitLab gives the real answer when it is pressed and a guess here
  // could lock out somebody it would have let through.
  //
  // The person comes before the blocker: somebody who may not merge here gains
  // nothing from being told to fix the pipeline first.
  //
  // The explanation for the caller who cannot merge is ALSO drawn on the card,
  // next to the buttons: a tooltip on a disabled button is the last place
  // anybody looks, and this is the sentence that tells them what to do next.
  const cannotMergeText =
    open && cannotMerge
      ? `Only people allowed to merge into ${open.target} can do this. ${whoCanMerge(open.reviewers ?? [])}`
      : ''
  const mergeReason = !allowed
    ? reason
    : !open
      ? 'Nothing is open for review on this branch. Propose it first.'
      : cannotMerge
        ? cannotMergeText
        : blocker?.blocking
          ? blocker.text
          : ''
  const mergeDisabled = !allowed || !open || cannotMerge || blockedFromMerging || busy
  // Which of the three is emphasised - the next thing to do, in the order the
  // work actually happens. All three stay reachable; only the weight moves.
  const primary = open ? 'merge' : merged ? 'finish' : 'propose'

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Version control</Typography>
        <Typography variant="body2" color="text.secondary">
          Your saves are commits on a work branch. This is where that branch is
          proposed for review, merged, and cleared up once it has merged.
        </Typography>
      </Box>

      <Typography variant="h6" component="h2">
        This work
      </Typography>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography sx={{ fontFamily: MONOSPACE, fontWeight: 600 }}>
            {branch || target}
          </Typography>
          <ArrowForwardIcon fontSize="small" color="disabled" />
          <Typography sx={{ fontFamily: MONOSPACE }} color="text.secondary">
            {target}
          </Typography>
          {issue ? <Chip label={`#${issue}`} size="small" /> : null}
          {situationChip(open, merged)}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading || busy}
          >
            Check again
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          BEST does not watch GitLab. Ask again after you merge.
        </Typography>
      </Paper>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Try again
            </Button>
          }
        >
          <AlertTitle>Could not read the repository</AlertTitle>
          {error.message}
        </Alert>
      ) : null}

      {/* Warnings are the reason this screen exists in the shape it does:
          GitLab honours a closing pattern only when the merge request targets
          the repository's OWN default branch, and when it does not, nothing
          anywhere says so - the issue simply stays open forever. */}
      {warnings.length ? (
        <Alert severity="warning">
          <AlertTitle>
            {warnings.length === 1 ? 'One thing to know' : `${warnings.length} things to know`}
          </AlertTitle>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </Stack>
        </Alert>
      ) : null}

      {state?.others?.length ? (
        <OtherProposals others={state.others} branch={state.branch} />
      ) : null}

      {outcome ? (
        <Alert severity={outcome.severity} onClose={() => setOutcome(null)}>
          <AlertTitle>{outcome.title}</AlertTitle>
          <Box sx={{ whiteSpace: 'pre-line' }}>{outcome.detail}</Box>
        </Alert>
      ) : null}

      {loading && !state ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {baseline ? (
        <Alert severity="info">
          <AlertTitle>Viewing a baseline</AlertTitle>
          {baseline} is a fixed, read-only copy of a tag, so there is nothing to propose, merge
          or finish. Compare it with {target} or another baseline below, or go back to
          {' '}{target} from the banner above.
        </Alert>
      ) : null}

      {onDefaultBranch ? (
        <Alert severity="info">
          <AlertTitle>Nothing in progress</AlertTitle>
          You are reading {target}, which is what everything merges into, so
          there is nothing to propose. Start work on an issue in the bar above
          and your saves become commits on its branch.
        </Alert>
      ) : null}

      {open ? (
        <Proposal
          proposal={open}
          heading="Open for review"
          blocker={blocker}
          refusal={refusal}
          showPeople
          // Writers only, like the other actions on this screen: changing who
          // is asked is a write to GitLab as this person.
          onChangeReviewers={
            allowed
              ? () => {
                  setDialogMode('reviewers')
                  setChangingReviewers(true)
                }
              : undefined
          }
          onAskOwners={allowed ? () => void askOwners(open) : undefined}
          askingOwners={askingOwners}
          // Not behind `allowed`: GitLab alone decides who may approve (#99).
          approval={<ApprovalRow slug={slug} proposal={open} onChanged={load} />}
        />
      ) : null}
      {open ? (
        // Under the card it is about. Controls follow the same writer check as
        // the other buttons; reading is open to anyone who can see the card.
        <DiscussionPanel
          slug={slug}
          iid={open.iid}
          canComment={allowed}
          reason={reason}
          onChanged={load}
          editTargetFor={(thread) => editTargetForThread(schema, changes, thread.element)}
          onEdit={setEditing}
          editReason={allowed ? '' : reason}
          refresh={discussionRefresh}
        />
      ) : null}
      {merged ? <Proposal proposal={merged} heading="Merged" /> : null}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
          <Tooltip title={proposeReason} describeChild>
            <span>
              <Button
                variant={primary === 'propose' ? 'contained' : 'outlined'}
                startIcon={<MergeTypeIcon />}
                onClick={() => {
                  setDialogMode('propose')
                  setProposing(true)
                }}
                disabled={!allowed || Boolean(open) || busy}
              >
                Propose for review
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={mergeReason} describeChild>
            <span>
              <Button
                variant={primary === 'merge' ? 'contained' : 'outlined'}
                startIcon={<MergeTypeIcon />}
                onClick={() => setMerging(true)}
                disabled={mergeDisabled}
              >
                Merge
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={finishReason} describeChild>
            <span>
              <Button
                variant={primary === 'finish' ? 'contained' : 'outlined'}
                startIcon={<DoneAllIcon />}
                onClick={() => setConfirming(true)}
                disabled={!allowed || Boolean(open) || busy}
              >
                Finish up
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {/* A sentence, not a control: who can merge it, said where the person
            is looking at the disabled button. The Change control for reviewers
            is on the card above. */}
        {cannotMergeText ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {cannotMergeText}
          </Alert>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Merging asks GitLab to merge, as you — so its protected-branch rules,
          approvals and pipeline requirements apply exactly as they would on its
          own page. Finishing is separate: it deletes this branch's working
          database and returns you to {target}, re-read from GitLab so it
          contains what merged.
        </Typography>
      </Paper>

      {finished ? <FinishSummary result={finished} target={target} /> : null}

      {/* Above the history deliberately. This is what the branch DID to the
          model; the history below is what landed on it as commits. A reviewer
          wants the first and reaches for the second to check it. */}
      {/* Neither is about a baseline: it has no branch diff and no commit history here. */}
      {baseline ? null : (
      <>
      <Paper variant="outlined">
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', px: 2, pt: 2, pb: 1, flexWrap: 'wrap', rowGap: 1 }}
        >
          <DifferenceIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            What {branch || target} changes in {target}
          </Typography>
          <TextField
            label="Link depth"
            select
            size="small"
            value={depth}
            onChange={(event) => setDepth(Number(event.target.value))}
            slotProps={{ select: { native: true } }}
            sx={{ width: 110 }}
            helperText="For review"
          >
            {[0, 1, 2, 3].map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </TextField>
          {/* The menu itself is shared with the baseline comparison (issue #97). */}
          <ChangesExportMenu
            disabled={changesLoading || !changes}
            urlFor={(fmt, part) =>
              api.changesExportUrl(slug, fmt === 'csv' ? { fmt, part, depth } : { fmt, depth })
            }
          />
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadChanges}
            disabled={changesLoading}
          >
            Refresh
          </Button>
        </Stack>
        <ChangeList
          changes={changes}
          loading={changesLoading}
          error={changesError}
          target={target}
          slug={slug}
          onEdit={setEditing}
          editReason={allowed ? '' : reason}
        />
      </Paper>

      <Paper variant="outlined">
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', px: 2, pt: 2, pb: 1 }}
        >
          <HistoryIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            History on {branch || target}
          </Typography>
          {/* Sizes match the other tables. Only the default branch has more
              history than fits: a work branch shows all of its own. */}
          {onDefaultBranch ? (
            <TextField
              label="Most recent"
              select
              size="small"
              value={historySize}
              onChange={(event) => {
                setHistorySize(Number(event.target.value))
                setPageAt({ branch, page: 1 })
              }}
              slotProps={{ select: { native: true } }}
              sx={{ width: 120 }}
            >
              {[25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </TextField>
          ) : null}
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadHistory}
            disabled={historyLoading}
          >
            Refresh
          </Button>
        </Stack>
        <CommitHistory
          branch={branch || target}
          commits={history?.items ?? []}
          loading={historyLoading}
          error={historyError}
          branchedFrom={history?.branched_from ?? null}
          target={target}
        />
        {/* No total, and no "page 2 of 9": GitLab leaves the count off a commit
            listing on a large repository, so all that is known is whether
            there is an older page. */}
        {onDefaultBranch && !historyError && (historyPage > 1 || history?.has_older) ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'flex-end', px: 2, py: 1 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
              Page {historyPage}, newest first
            </Typography>
            <Button
              size="small"
              onClick={() => setPageAt({ branch, page: historyPage - 1 })}
              disabled={historyLoading || historyPage <= 1}
            >
              Newer
            </Button>
            <Button
              size="small"
              onClick={() => setPageAt({ branch, page: historyPage + 1 })}
              disabled={historyLoading || !history?.has_older}
            >
              Older
            </Button>
          </Stack>
        ) : null}
      </Paper>
      </>
      )}

      {/* The program's baselines (issue #97): about the program, not this branch. */}
      <BaselinesSection slug={slug} target={target} canCut={!principal.anonymous && !singleDatabase} />

      {/* Issue #110: the element's own editor, over the discussion. It saves to the
          branch this screen shows. After a save the change list and the threads
          are re-read; the thread itself is left for the person to resolve. */}
      {editing && allowed ? (
        <EditElementDialog
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            loadChanges()
            setDiscussionRefresh((count) => count + 1)
          }}
        />
      ) : null}

      <ProposeMergeDialog
        open={proposing || changingReviewers}
        slug={slug}
        branch={branch || target}
        target={target}
        issue={issue}
        existing={open}
        mode={dialogMode}
        onClose={() => {
          setProposing(false)
          setChangingReviewers(false)
        }}
        onProposed={dialogMode === 'reviewers' ? reviewersSaved : proposed}
      />

      <ConfirmDialog
        open={merging}
        title={`Merge !${open?.iid ?? ''} into ${target}?`}
        message={`GitLab performs the merge, as you — so its protected-branch rules, approval rules and pipeline requirements all apply. If any of them is unmet, or you are not one of the people allowed to merge here, GitLab refuses, BEST shows you what it said, and nothing happens.

The branch is deleted by GitLab on merge. Your working database is NOT: finishing up is what removes that, and it is a separate step.`}
        confirmLabel="Merge"
        severity="info"
        busy={busy}
        onConfirm={() => void merge()}
        onCancel={() => setMerging(false)}
      />

      <ConfirmDialog
        open={confirming}
        title={`Finish ${branch || target}?`}
        message={`The branch's working database is deleted. It was only a cache of a branch GitLab removes when it merges, and nothing in the repository is touched.\n\nYou are then returned to ${target}, which BEST re-reads from GitLab so it contains what just merged.\n\nIf the work has not merged, this is refused and nothing is lost.`}
        confirmLabel="Finish up"
        severity="info"
        busy={busy}
        onConfirm={() => void finish()}
        onCancel={() => setConfirming(false)}
      />
    </Stack>
  )
}

/**
 * One merge request, as a card that links out.
 *
 * The link is the point: BEST opens merge requests and GitLab reviews them, so
 * everything a person does next happens over there.
 *
 * `blocker` and `refusal` are passed in for the OPEN card only, and that is on
 * purpose. A merged request still carries a status (`not_open`), and reading
 * "this merge request is not open" under a card headed Merged would be a
 * blocker for something that already got through. `showPeople` follows the same
 * line: who a merge request is assigned to and who was asked to review it is
 * about getting it merged, which a merged one no longer needs.
 */
function Proposal({
  proposal,
  heading,
  blocker = null,
  refusal = null,
  showPeople = false,
  onChangeReviewers,
  onAskOwners,
  askingOwners = false,
  approval,
}: {
  proposal: MergeRequest
  heading: string
  blocker?: MergeBlocker | null
  refusal?: Refusal | null
  showPeople?: boolean
  /** Opens the reviewers editor. Absent for a reader, who gets no button. */
  onChangeReviewers?: () => void
  /** Asks the owners nobody has asked yet. Absent for a reader, who gets no button. */
  onAskOwners?: () => void
  askingOwners?: boolean
  /** The approvals line, drawn under the owners. Open card only. */
  approval?: ReactNode
}) {
  const unasked = unaskedOwners(proposal)
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            {heading}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: MONOSPACE }} color="text.secondary">
              !{proposal.iid}
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>{proposal.title}</Typography>
            {proposal.draft ? <Chip label="Draft" size="small" /> : null}
            {proposal.labels.map((label) => (
              <Chip key={label} label={label} size="small" variant="outlined" />
            ))}
          </Stack>
          <Typography variant="caption" sx={{ fontFamily: MONOSPACE }} color="text.disabled">
            {proposal.source} → {proposal.target}
          </Typography>
          {/* Shown, not judged: GitLab holds who is assigned and who was asked,
              and BEST lists them. A payload from before issue #67 carries
              neither array, which reads as nobody. */}
          {showPeople ? (
            <Stack direction="row" spacing={3} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
              <PeopleLine label="Assigned to" people={proposal.assignees ?? []} />
              <PeopleLine
                label="Reviewers"
                people={proposal.reviewers ?? []}
                branch={proposal.target}
                action={
                  onChangeReviewers ? (
                    <Button size="small" onClick={onChangeReviewers} sx={{ minWidth: 0, py: 0 }}>
                      Change
                    </Button>
                  ) : undefined
                }
              />
            </Stack>
          ) : null}
          {/* Who GitLab says owns what this changes. Absent entirely when none
              are known, so a repository with no CODEOWNERS looks as it always
              did. A line of information with one optional click, never a gate. */}
          {showPeople && (proposal.owners ?? []).length > 0 ? (
            <OwnersLine
              owners={proposal.owners ?? []}
              askable={unasked.length > 0 ? onAskOwners : undefined}
              asking={askingOwners}
            />
          ) : null}
          {showPeople ? approval : null}
        </Box>
        <Button
          startIcon={<LaunchIcon />}
          href={proposal.web_url}
          target="_blank"
          rel="noreferrer"
        >
          Open in GitLab
        </Button>
      </Stack>

      {/* What GitLab says is in the way, in words. Absent when it is mergeable
          or GitLab said nothing - null is not "no", so nothing is invented. */}
      {blocker ? (
        <Alert severity={blocker.severity} sx={{ mt: 1.5 }}>
          {blocker.text}
        </Alert>
      ) : null}

      {/* GitLab's answer to pressing Merge, kept here rather than flashed as a
          toast: it is about this merge request and is what the person looks
          back at. Cleared by the next attempt or by asking again. */}
      {refusal ? (
        <Alert severity={refusal.severity} sx={{ mt: 1.5 }}>
          <AlertTitle>{refusal.title}</AlertTitle>
          <Box sx={{ whiteSpace: 'pre-line' }}>{refusal.detail}</Box>
        </Alert>
      ) : null}
    </Paper>
  )
}

/**
 * "Assigned to: Ann" or "Reviewers: Bo, Cy", with the can-merge mark where
 * GitLab says so (`branch` given only for reviewers, who are the ones marked).
 * Nobody reads as "nobody yet". `action` is a control drawn at the end of the
 * line, for the one line that can be edited.
 */
/**
 * Merge requests open for this work's issue from some OTHER branch.
 *
 * Issue #98. BEST finds a merge request by its source branch, so one opened
 * from GitLab's issue page onto a fresh `-2` copy of the branch was invisible
 * here - this screen said "not proposed" while a reviewer was looking at an
 * empty review. What to do differs by whether it has anything in it, so each
 * says which; BEST closes nothing itself.
 */
function OtherProposals({ others, branch }: { others: OtherProposal[]; branch: string }) {
  return (
    <Alert severity="warning">
      <AlertTitle>
        {others.length === 1
          ? 'This issue has a merge request on another branch'
          : `This issue has ${others.length} merge requests on other branches`}
      </AlertTitle>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
        {others.map((other) => (
          <li key={other.iid}>
            <Link href={other.web_url} target="_blank" rel="noreferrer">
              !{other.iid} {other.title}
            </Link>{' '}
            is from{' '}
            <Box component="span" sx={{ fontFamily: MONOSPACE }}>
              {other.source}
            </Box>
            , not {branch}.{' '}
            {other.empty === true
              ? 'It has no commits, so there is nothing in it to review. Close it in GitLab, and propose from here.'
              : other.empty === false
                ? 'It has commits of its own, so the work is split across two branches. Settle which one carries it before proposing.'
                : 'GitLab would not say whether it has anything in it.'}
          </li>
        ))}
      </Stack>
    </Alert>
  )
}

function PeopleLine({
  label,
  people,
  branch,
  action,
}: {
  label: string
  people: PersonMergeHint[]
  branch?: string
  action?: ReactNode
}) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary">
        {label}:
      </Typography>
      {people.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          nobody yet
        </Typography>
      ) : (
        people.map((person) => (
          <Stack key={person.id} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography variant="caption">{person.name}</Typography>
            {branch && person.can_merge === true ? <CanMergeMark branch={branch} /> : null}
          </Stack>
        ))
      )}
      {action}
    </Stack>
  )
}

/**
 * The owners nobody has asked to review yet - the ones "Ask them" would add.
 *
 * The AUTHOR is left out when the payload says who it is: asking yourself for a
 * review is noise, and the server leaves them off at creation for the same
 * reason. When the payload does not identify the author (`author` absent or
 * null) every unasked owner counts, and that is accepted rather than guessed
 * at - the assignee is not a stand-in, since anyone can be reassigned.
 */
function unaskedOwners(proposal: MergeRequest): CodeOwner[] {
  const authorId = proposal.author?.id
  return (proposal.owners ?? []).filter((owner) => !owner.is_reviewer && owner.id !== authorId)
}

/**
 * "Code owners: Jon Wirtzfeld — BALROG Flight Software  asked", with an
 * "Ask them" button when `askable` is given.
 *
 * The owners are READ from GitLab (its approval rules for this merge request;
 * BEST never reads CODEOWNERS and keeps no list), so what is drawn is whatever
 * GitLab named, with the section(s) each person owns for this change. "asked"
 * is a quiet mark for those already among the reviewers. Owners are asked,
 * never required: no wording or control here blocks or disables anything.
 */
function OwnersLine({
  owners,
  askable,
  asking,
}: {
  owners: CodeOwner[]
  askable?: () => void
  asking: boolean
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
    >
      <Typography variant="caption" color="text.secondary">
        Code owners:
      </Typography>
      {owners.map((owner) => (
        <Stack key={owner.id} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography variant="caption">
            {owner.sections.length ? `${owner.name} — ${owner.sections.join(', ')}` : owner.name}
          </Typography>
          {owner.is_reviewer ? (
            <Typography variant="caption" color="text.disabled">
              asked
            </Typography>
          ) : null}
        </Stack>
      ))}
      {askable ? (
        <Button size="small" onClick={askable} disabled={asking} sx={{ minWidth: 0, py: 0 }}>
          {asking ? 'Asking…' : 'Ask them'}
        </Button>
      ) : null}
    </Stack>
  )
}

/**
 * Who could merge it, as a sentence for the Merge button's reason - said only
 * when GitLab has told this person they may not. It names; it does not gate:
 * the reviewers' `can_merge` is GitLab's hint, and BEST requires nobody.
 * Reached with `can_merge === false` for the caller, so it only ever adds to
 * that explanation.
 */
function whoCanMerge(reviewers: PersonMergeHint[]): string {
  const able = reviewers.filter((person) => person.can_merge === true).map((person) => person.name)
  if (able.length === 0) {
    return 'Nobody asked to review it can merge — add a reviewer who can.'
  }
  const names =
    able.length === 1 ? able[0] : `${able.slice(0, -1).join(', ')} and ${able[able.length - 1]}`
  return `Ask ${names} to merge it.`
}

/** What finishing actually did, since most of it happened out of sight. */
function FinishSummary({ result, target }: { result: FinishResult; target: string }) {
  return (
    <Alert severity={result.warnings.length ? 'warning' : 'success'}>
      <AlertTitle>
        {result.merged ? `${result.branch} merged` : `${result.branch} cleared up`}
      </AlertTitle>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
        <li>
          {result.reaped
            ? "The branch's working database has been deleted."
            : 'There was no working database left to delete.'}
        </li>
        <li>
          {result.refreshed
            ? `${target} was re-read from GitLab: ${result.refreshed.files} files, ${result.refreshed.requirements} requirements, ${result.refreshed.verification} verification elements.`
            : `${target} was not re-read, so what you are looking at may predate the merge.`}
        </li>
        {result.moved.length ? (
          <li>
            {result.moved.join(', ')} {result.moved.length === 1 ? 'was' : 'were'} also
            on {result.branch} and {result.moved.length === 1 ? 'has' : 'have'} been put
            back on {target}.
          </li>
        ) : null}
        {result.warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </Stack>
    </Alert>
  )
}
